import React, { useState } from 'react';
import { Building, Phone, Mail, Tag, MapPin, DollarSign, Upload, Eye, X, FileText, Download, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from '../../api/axios';

const VendorDetailsModal = ({ vendor, onClose }) => {
  const [uploading, setUploading] = useState(false);
  const [documentType, setDocumentType] = useState('Trade License');

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'INACTIVE': return 'bg-gray-100 text-gray-800';
      case 'SUSPENDED': return 'bg-red-100 text-red-800';
      case 'PENDING_APPROVAL': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'SPARE_PARTS': return 'bg-blue-100 text-blue-800';
      case 'CONSUMABLES': return 'bg-purple-100 text-purple-800';
      case 'SERVICE_PROVIDER': return 'bg-green-100 text-green-800';
      case 'EQUIPMENT': return 'bg-orange-100 text-orange-800';
      case 'TOOLS': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);

    try {
      const response = await axios.post(`/api/vendors/${vendor.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      // Add new document to the list without refreshing
      vendor.documents = vendor.documents || [];
      vendor.documents.push(response.data);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await axios.delete(`/api/vendors/documents/${documentId}`);
        toast.success('Document deleted successfully');
        // Remove document from the list without refreshing
        vendor.documents = vendor.documents.filter(doc => doc.id !== documentId);
      } catch (error) {
        toast.error('Failed to delete document');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Vendor Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Vendor Info */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                  <Building className="w-8 h-8 text-blue-600" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">{vendor.name}</h4>
                <span className={`px-3 py-1 text-sm rounded-full ${getTypeColor(vendor.type)}`}>
                  {vendor.type.replace('_', ' ')}
                </span>
                <div className="mt-2">
                  <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(vendor.status)}`}>
                    {vendor.status.replace('_', ' ')}
                  </span>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                {vendor.contactPerson && (
                  <div className="flex items-center">
                    <Building className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{vendor.contactPerson}</span>
                  </div>
                )}
                {vendor.email && (
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{vendor.email}</span>
                  </div>
                )}
                {vendor.phone && (
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{vendor.phone}</span>
                  </div>
                )}
                {vendor.website && (
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 text-gray-400 mr-2" />
                    <a
                      href={vendor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {vendor.website}
                    </a>
                  </div>
                )}
              </div>

              {/* Tags */}
              {vendor.tags && vendor.tags.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center mb-2">
                    <Tag className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Tags</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {vendor.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Business Information */}
            <div className="bg-white border rounded-lg p-4">
              <h5 className="text-lg font-medium text-gray-900 mb-4">Business Information</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <p className="text-sm font-medium text-gray-900">{vendor.category || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">TRN</p>
                  <p className="text-sm font-medium text-gray-900">{vendor.trn || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">VAT ID</p>
                  <p className="text-sm font-medium text-gray-900">{vendor.vatId || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Terms</p>
                  <p className="text-sm font-medium text-gray-900">{vendor.paymentTerms || 'Not specified'}</p>
                </div>
                {vendor.creditLimit && (
                  <div>
                    <p className="text-sm text-gray-500">Credit Limit</p>
                    <p className="text-sm font-medium text-gray-900">${vendor.creditLimit}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Member Since</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(vendor.createdAt)}</p>
                </div>
              </div>
            </div>

            {/* Address */}
            {vendor.address && (
              <div className="bg-white border rounded-lg p-4">
                <h5 className="text-lg font-medium text-gray-900 mb-4">Address</h5>
                <div className="flex items-start">
                  <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-1" />
                  <p className="text-sm text-gray-900">{vendor.address}</p>
                </div>
              </div>
            )}

            {/* Purchase Statistics */}
            {vendor.purchaseStats && (
              <div className="bg-white border rounded-lg p-4">
                <h5 className="text-lg font-medium text-gray-900 mb-4">Purchase Statistics</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500">Total Orders</p>
                      <p className="text-lg font-semibold text-gray-900">{vendor.purchaseStats.totalOrders}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <DollarSign className="w-4 h-4 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500">Total Amount</p>
                      <p className="text-lg font-semibold text-gray-900">${vendor.purchaseStats.totalAmount}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Documents */}
            <div className="bg-white border rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h5 className="text-lg font-medium text-gray-900">Documents</h5>
                <div className="flex items-center space-x-2">
                  <select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="Trade License">Trade License</option>
                    <option value="Agreement">Agreement</option>
                    <option value="Certificate">Certificate</option>
                    <option value="Other">Other</option>
                  </select>
                  <label className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded cursor-pointer hover:bg-blue-700">
                    <Upload className="w-4 h-4 mr-1" />
                    {uploading ? 'Uploading...' : 'Upload'}
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    />
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                {vendor.documents && vendor.documents.length > 0 ? (
                  vendor.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-2 border rounded">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{doc.fileName}</p>
                          <p className="text-xs text-gray-500">{doc.documentType} • {formatDate(doc.uploadedAt)}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => window.open(`http://localhost:5000${doc.filePath}`, '_blank')}
                          className="text-green-600 hover:text-green-800"
                          title="View document"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => window.open(`http://localhost:5000${doc.filePath}`, '_blank')}
                          className="text-blue-600 hover:text-blue-800"
                          title="Download document"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete document"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No documents uploaded</p>
                )}
              </div>
            </div>



            {/* Notes */}
            {vendor.notes && (
              <div className="bg-white border rounded-lg p-4">
                <h5 className="text-lg font-medium text-gray-900 mb-4">Notes</h5>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{vendor.notes}</p>
              </div>
            )}

            {/* Recent Purchase Orders */}
            {vendor.purchaseOrders && vendor.purchaseOrders.length > 0 && (
              <div className="bg-white border rounded-lg p-4">
                <h5 className="text-lg font-medium text-gray-900 mb-4">Recent Purchase Orders</h5>
                <div className="space-y-2">
                  {vendor.purchaseOrders.slice(0, 5).map((order) => (
                    <div key={order.id} className="flex items-center justify-between p-2 border rounded">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{order.orderNumber}</p>
                        <p className="text-xs text-gray-500">{formatDate(order.orderDate)} • {order.status}</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">${order.totalAmount}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorDetailsModal;
